import { AddMembersIcon } from '@sketchnote/icons';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import React from 'react';

import styles from './avatar.module.css';

export interface AvatarProps {
  size?: 'xl' | 'lg' | 'md' | 'sm' | 'sm1' | 'xs' | 'xs1';
  src?: string;
  imgClassName?: string;
  wrapperClassName?: string;
  name: string | number;
  type?: 'initials' | 'photo' | 'number' | 'add-member' | 'notification';
  onClick?: (event: any) => void;
  tooltip?: boolean;
}

export const Avatar = React.memo(
  ({
    size = 'md',
    src,
    wrapperClassName,
    name,
    type,
    onClick,
    tooltip = true,
  }: AvatarProps): JSX.Element => {
    const wrapperClasses = classNames({
      [wrapperClassName || '']: !!wrapperClassName,
      'text-gray-700 bg-gray-200': type === 'number',
      'rounded-full inline-block overflow-hidden font-medium flex justify-center items-center bg-cover bg-center':
        true,
      'w-64 h-64': size === 'xl',
      'w-40 h-40': size === 'lg',
      'w-32 h-32 text-sm': size === 'md',
      'w-24 h-24 text-xs': size === 'sm',
      'w-28 h-28 text-xs': size === 'sm1',
      'w-20 h-20 text-xs': size === 'xs1',
      'w-16 h-16 text-xss': size === 'xs',
      'bg-primary-100 text-primary-500':
        type === 'initials' || type === 'photo' || type === 'add-member',
      'bg-gray-200 text-gray-850': type === 'notification',
    });

    const label =
      typeof name === 'string'
        ? name.split(' ').length > 1
          ? name
              .split(' ')
              .map((text) => (text ? text[0].toUpperCase() : ''))
              .join('')
          : name?.[0]
        : typeof name === 'number'
        ? `+${name}`
        : name;

    const userImage = (
      <span
        className={wrapperClasses}
        role="button"
        onClick={onClick}
        tabIndex={-1}
        onKeyDown={() => null}
        style={{ backgroundImage: `url(${src})` }}
      >
        {type === 'add-member' ? (
          <AddMembersIcon className="w-full h-full" size={24} />
        ) : (
          !src && label
        )}
      </span>
    );
    return (
      <>
        {tooltip ? (
          <Tippy
            className="w-auto h-auto bg-gray-999 tippy"
            content={name}
            placement="bottom"
            arrow={false}
          >
            {userImage}
          </Tippy>
        ) : (
          userImage
        )}
      </>
    );
  }
);

export interface AvatarGroupProps {
  children: React.ReactNode;
  maxCount?: number;
  className?: string;
}

export const AvatarGroup = ({ children, maxCount, className: classes = '' }: AvatarGroupProps) => {
  const className = `${styles['sn-avatar-group']} inline-flex ${classes}`;
  const childrenWithProps = React.Children.map(children, (child, index) => {
    return child
      ? React.cloneElement(child as any, {
          key: `avatar-key-${index}`,
          wrapperClassName: `${styles['sn-avatar']} border-2 border-white ${
            (child as any)?.props?.wrapperClassName
          }`,
        })
      : null;
  })?.filter((child) => child);

  const numOfChildren = childrenWithProps?.length;
  if (numOfChildren && maxCount && maxCount < numOfChildren) {
    const childrenShow = childrenWithProps?.slice(0, maxCount);
    if (childrenWithProps) childrenShow?.push(childrenWithProps[numOfChildren - 1]);
    return <div className={className}>{childrenShow ? childrenShow : null}</div>;
  }

  return <div className={className}>{childrenWithProps}</div>;
};
